// src/components/frontpage/columns/GroupEventsColumn.jsx
//
// Group-context Events column for the Group front page / mobile Events tab (Groups Tier 1).
//
// The mauve-context sibling of EventsColumn. Card layout / date grouping is identical (it is
// duplicated here rather than shared because the build brief requires the locality EventsColumn
// to stay untouched — Option A). The ONLY behavioural difference is the "+" create handler:
// it dispatches `new-event` WITH groupId, so AddEditEvent receives preselectedGroupId and the
// event is anchored to the group (community_id), not the locality.
//
// FUTURE/PAST (Events tab): this column owns the roster split. It receives the FULL list of the
// group's events (the parent no longer pre-filters to the feed window) and splits them with
// hasEventEnded — Future (default) = not ended, soonest first; Past = ended, most recent first.
// display_from is ignored here (it is a feed-scheduling control, not a roster filter).
//
// FOLLOW-UP (noted for a later cleanup tier): extract the shared event-card rendering so the
// locality and group columns stop duplicating ~150 lines of presentation.

import React, { useMemo, useState } from 'react';
import { useTheme } from '../../../themes/ThemeProvider';
import { useTranslation } from '../../../i18n/useTranslation';
import { useAuth } from '../../../contexts/AuthContext';
import ModeColumn from './ModeColumn';
import { parseEventDate, hasEventEnded } from '../../../utils/eventDateTime';
import { useTagHouseFallback } from '../../../hooks/useTagHouseFallback';

// Inline fallback for events without images
const FALLBACK_IMAGE = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='48' height='40' viewBox='0 0 48 40' preserveAspectRatio='xMidYMid slice' fill='%23e5e7eb'%3E%3Crect width='48' height='40'/%3E%3Ctext x='50%25' y='50%25' dominant-baseline='middle' text-anchor='middle' fill='%239ca3af' font-size='12'%3E%F0%9F%93%85%3C/text%3E%3C/svg%3E";

// canCreate: false while the group is closing (Roadmap #103) — the "+" is not offered; the database
// refuses a new event anyway.
const GroupEventsColumn = ({ data = [], onClick, group, hideColumnHeader = false, canCreate = true }) => {
  const theme = useTheme();
  const { t, formattingLocale } = useTranslation('frontpage');
  const { requireAuth } = useAuth();
  // R4 house fallback (image-layer build) — null until fetched, so the inline
  // SVG below keeps rendering as the interim default, no blank-to-image flash.
  const houseFallback = useTagHouseFallback();

  // Future (default) | Past
  const [view, setView] = useState('future');


  // Format the start time for the card line: "19:00". The day is in the date separator above.
  const formatEventTime = (dateStr) => {
    const date = parseEventDate(dateStr);
    if (!date) return '';
    return date.toLocaleTimeString(formattingLocale, {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });
  };

  // Format a date separator header: "Wednesday, 24 June" (omit year unless different)
  const formatDateSeparator = (dateStr) => {
    if (!dateStr) return '';
    const date = parseEventDate(dateStr);
    if (!date) return '';
    const now = new Date();
    const options = { weekday: 'long', day: 'numeric', month: 'long' };
    if (date.getFullYear() !== now.getFullYear()) options.year = 'numeric';
    return date.toLocaleDateString(formattingLocale, options);
  };

  // Get a date-only key (YYYY-MM-DD) in local timezone for grouping
  const getDateKey = (dateStr) => {
    if (!dateStr) return 'none';
    const d = parseEventDate(dateStr);
    if (!d) return 'none';
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  };

  // Null-safe start-date comparators (events without a start sink to the bottom)
  const byStartAsc = (a, b) => {
    const da = parseEventDate(a.start_date);
    const db = parseEventDate(b.start_date);
    if (!da && !db) return 0;
    if (!da) return 1;
    if (!db) return -1;
    return da - db;
  };

  // Split the full roster into Future / Past (display_from ignored — this is the roster, not the feed).
  const { future, past } = useMemo(() => {
    const fut = [];
    const pst = [];
    for (const event of data) {
      (hasEventEnded(event) ? pst : fut).push(event);
    }
    fut.sort(byStartAsc);                 // soonest first
    pst.sort((a, b) => -byStartAsc(a, b)); // most recent first
    return { future: fut, past: pst };
  }, [data]); // eslint-disable-line react-hooks/exhaustive-deps

  const activeList = view === 'past' ? past : future;

  // Group the active list by calendar day (separators)
  const groupedEvents = useMemo(() => {
    const groups = [];
    let currentKey = null;
    for (const event of activeList) {
      const key = getDateKey(event.start_date);
      if (key !== currentKey) {
        currentKey = key;
        groups.push({ dateKey: key, label: formatDateSeparator(event.start_date), events: [] });
      }
      groups[groups.length - 1].events.push(event);
    }
    return groups;
  }, [activeList, formattingLocale]); // eslint-disable-line react-hooks/exhaustive-deps

  // Group-scoped create: dispatch new-event WITH groupId so the event anchors to this group.
  const handleNewEvent = () => {
    requireAuth('gate_body_add_event', () => {
      window.dispatchEvent(new CustomEvent('ournet:navigate', {
        detail: { view: 'new-event', mode: 'add', groupId: group?.id || null }
      }));
    });
  };

  // Compact two-segment Future|Past pill — themed by context accent (mauve group / blue locality).
  // Rendered via ModeColumn's headerRight slot, inline with the title + "+" button.
  const toggle = (
    <div style={{
      display: 'inline-flex', borderRadius: '999px', overflow: 'hidden',
      border: `1px solid ${theme.primary}`, flexShrink: 0
    }}>
      {[['future', t('events:toggle_future')], ['past', t('events:toggle_past')]].map(([key, label]) => {
        const active = view === key;
        return (
          <button
            key={key}
            onClick={() => setView(key)}
            style={{
              border: 'none', cursor: 'pointer', padding: '3px 12px',
              fontFamily: 'OpenSauceSans, sans-serif', fontSize: '12px', fontWeight: 700,
              background: active ? theme.primary : 'transparent',
              color: active ? '#fff' : theme.primary
            }}
          >
            {label}
          </button>
        );
      })}
    </div>
  );

  // Compact card: thumbnail + title + time·venue, no borders (sits in a 2-per-line grid cell).
  const eventRow = (event) => (
    <div
      key={event.id}
      onClick={() => onClick?.(event)}
      style={{
        display: 'flex', alignItems: 'center', gap: '9px',
        padding: '7px', minWidth: 0,
        background: 'white',
        borderRadius: '8px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
        cursor: onClick ? 'pointer' : 'default',
        transition: 'box-shadow 0.15s'
      }}
      onMouseEnter={(e) => { if (onClick) e.currentTarget.style.boxShadow = '0 2px 6px rgba(0,0,0,0.12)'; }}
      onMouseLeave={(e) => { e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.08)'; }}
    >
      <img
        src={event.cover_image_url || houseFallback || FALLBACK_IMAGE}
        alt=""
        loading="lazy"
        // §5a — never crop the illustration's own border; contain + this
        // card's background (white, per its container `background` above) as
        // the letterbox fill.
        style={{ width: '48px', height: '40px', objectFit: 'contain', backgroundColor: 'white', flexShrink: 0, borderRadius: '6px' }}
        onError={(e) => { e.target.onerror = null; e.target.src = houseFallback || FALLBACK_IMAGE; }}
      />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          fontFamily: 'OpenSauceSans, sans-serif', fontWeight: '600', fontSize: '13px',
          color: '#1f2937', lineHeight: '1.25',
          overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical'
        }}>
          {event.title}
        </div>
        <div style={{
          fontFamily: 'OpenSauceSans, sans-serif', fontSize: '11px', color: '#6b7280',
          marginTop: '1px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'
        }}>
          {formatEventTime(event.start_date)}
          {event.venue_name ? ` · ${event.venue_name}` : ''}
        </div>
      </div>
    </div>
  );

  return (
    <ModeColumn
      title={t('column_events')}
      themeColor={theme.primary}
      hideColumnHeader={hideColumnHeader}
      onAdd={canCreate ? handleNewEvent : undefined}
      addLabel={t('new_event')}
      isEmpty={data.length === 0}
      emptyLabel={t('empty_events')}
      headerRight={toggle}
    >
      {activeList.length === 0 ? (
        <div style={{
          fontFamily: 'OpenSauceSans, sans-serif', fontSize: '12px', color: '#9ca3af',
          textAlign: 'center', padding: '20px 8px'
        }}>
          {t('empty_events')}
        </div>
      ) : (
        groupedEvents.map(grp => (
          <div key={grp.dateKey} style={{ marginBottom: '10px' }}>
            {grp.label && (
              <div style={{
                fontFamily: 'OpenSauceSans, sans-serif', fontWeight: '700', fontSize: '13px',
                color: theme.primary, background: 'white', border: `1px solid ${theme.primary}`,
                textTransform: 'uppercase', letterSpacing: '0.3px', padding: '4px 8px',
                borderRadius: '6px', margin: '0 0 6px', lineHeight: '1.3'
              }}>
                {grp.label}
              </div>
            )}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {grp.events.map(eventRow)}
            </div>
          </div>
        ))
      )}
    </ModeColumn>
  );
};

export default GroupEventsColumn;
